// All 12 service pages. Each entry drives an /services/:slug (or /ai-lab)
// page rendered by <InnerPage />. Content is verbatim from the client brief;
// do not paraphrase.

export const SERVICES = {
  "custom-software-development-company-in-oman": {
    slug: "custom-software-development-company-in-oman",
    path: "/services/custom-software-development-company-in-oman",
    navLabel: "Custom software development",
    eyebrow: "Services",
    h1: "Custom Software Development Company in Oman",
    meta: {
      title: "Custom Software Development Company in Oman",
      description: "Custom software from an Oman-based company. Web, mobile, integrations and AI, designed for PDPL obligations.",
    },
    intro: "Wafa Solutions LLC is an Oman-based technology company and the technology partner to Altalib Group. We build custom software for businesses across Oman and the region. When the off-the-shelf tool does not fit how you actually work, we build the one that does — designed around your process instead of forcing you into someone else's.",
    sections: [
      {
        type: "block", heading: "When custom is right",
        bullets: [
          "Your process is your competitive advantage and the software flattens it",
          "You are paying for five tools that do not talk to each other",
          "Your team spends hours a week moving data by hand",
          "Licence costs climb every time you hire",
          "You need Arabic, or something the local market simply does not have",
        ],
      },
      { type: "callout", text: "We will tell you which one you are. If a configured off-the-shelf tool at 200 rials a month solves it, we would rather say so than take your build fee." },
      {
        type: "grid", eyebrow: "What we build", heading: "Six shapes of custom software",
        items: [
          { title: "Web platforms", desc: "Portals, internal tools and public products." },
          { title: "Mobile applications", desc: "iOS and Android, built from a shared codebase." },
          { title: "Integration layers", desc: "Making systems you already own talk to each other." },
          { title: "APIs", desc: "Public and partner APIs, documented and versioned." },
          { title: "Automation", desc: "Workflows that remove real manual work — with an audit trail." },
          { title: "Migrations", desc: "Old system to new, without losing the last five years of data." },
        ],
        columns: 3,
      },
      {
        type: "stack", heading: "Our stack",
        subheading: "Chosen because it lasts, not because it's fashionable.",
        items: ["React", "Next.js", "TypeScript", "Tailwind", "React Native / Expo", "Python", "FastAPI", "Node", "MongoDB", "PostgreSQL", "Redis", "Managed cloud", "Docker", "CI/CD"],
      },
      {
        type: "bullets", heading: "What comes as standard",
        items: [
          "Role-based access", "Full audit trails", "English and Arabic",
          "Responsive across screen sizes", "Reporting and Excel export",
          "Automated backups", "Error monitoring", "Documented API",
        ],
      },
      { type: "process" },
      {
        type: "faq", heading: "Common questions",
        items: [
          { q: "How long does a custom project take?", a: "A focused internal tool typically runs six to ten weeks. A platform with mobile apps and a dashboard is more like three to six months. We break every project into two-week sprints, so you see working software every fortnight." },
          { q: "What does it cost?", a: "A small internal tool starts from the low thousands of rials. A multi-app platform runs into the tens of thousands. Before you commit we send a written breakdown by module so nothing lands as a surprise." },
          { q: "Do we own the code?", a: "Yes on final payment. Our reusable accelerator components remain licensed to you, which is what keeps your price down — you don't pay to have the same building blocks written from scratch every project." },
          { q: "Can you take over a system someone else built?", a: "Yes. We run a ten-working-day audit, then give you an honest fix / rebuild / leave recommendation. Sometimes the answer is that the system is fine and you just need someone to watch it." },
          { q: "Where will it be hosted?", a: "We go through the options with you first — cost, performance, and what each means for your PDPL obligations — so the choice is deliberate, documented, and reversible if a regulator or customer asks a harder question next year." },
          { q: "Do you sign an NDA?", a: "Yes, before the first detailed conversation." },
        ],
      },
    ],
    related: [
      { tag: "Services", label: "SaaS development", desc: "Multi-tenant products designed to sell.", to: "/services/saas-development-company-in-oman" },
      { tag: "Services", label: "Application support & maintenance", desc: "Look after what you already have.", to: "/services/application-support-maintenance-oman" },
      { tag: "Work", label: "Delivery & logistics platform", desc: "Ready for launch in Oman.", to: "/work/delivery-logistics-platform" },
    ],
  },

  "saas-development-company-in-oman": {
    slug: "saas-development-company-in-oman",
    path: "/services/saas-development-company-in-oman",
    navLabel: "SaaS development",
    eyebrow: "Services",
    h1: "SaaS Development Company in Oman",
    meta: {
      title: "SaaS Development Company in Oman",
      description: "SaaS products from the first screen to the billing engine, built for the questions your customers here will ask about data.",
    },
    intro: "We build SaaS platforms from the first screen to the billing engine. Multi-tenant architecture, subscription management, admin consoles, analytics and cloud deployment — built by an Oman-based company that understands what your customers here will ask you about data.",
    sections: [
      {
        type: "block", heading: "Building a product is not building a project",
        paragraphs: [
          "A project has one client. A product has many, and every one will use it differently, at a different scale, and want their own logo on it.",
          "That changes the architecture, the pricing model, the support model and the roadmap. Building a SaaS like a project is the most expensive mistake we see.",
        ],
      },
      {
        type: "grid", eyebrow: "What we build", heading: "Seven building blocks of a SaaS",
        items: [
          { title: "SaaS architecture", desc: "Multi-tenant from day one, so tenant #200 is not a rebuild." },
          { title: "Subscription & billing", desc: "Plans, trials, upgrades, dunning — with local payments." },
          { title: "Tenant & admin portals", desc: "Self-service where possible; superadmin where necessary." },
          { title: "Analytics dashboards", desc: "Both product analytics for you and usage reports for your tenants." },
          { title: "APIs & integrations", desc: "Public APIs so your customers can build on your platform." },
          { title: "Cloud & DevOps", desc: "Automated deploys, observability, incident playbook." },
          { title: "White-label", desc: "Per-tenant branding, custom domains, isolated data." },
        ],
      },
      {
        type: "grid", heading: "Types of SaaS we build", columns: 4,
        items: [
          { title: "Vertical SaaS", desc: "Deep for one industry." },
          { title: "Horizontal tools", desc: "Broad utility across industries." },
          { title: "Internal turned product", desc: "You built it for yourself; now you want to sell it." },
          { title: "White-label platforms", desc: "Your customers rebrand and resell." },
        ],
      },
      {
        type: "bullets", heading: "What comes as standard",
        items: [
          "Tenant isolation", "Trials", "Usage metering", "Billing", "Per-tenant permissions",
          "Onboarding flow", "In-app support", "Admin impersonation", "Audit logging", "Bilingual",
        ],
      },
      { type: "callout", text: "Why us: we do not just build SaaS, we sell it — Meyar, our showroom platform and our warehouse system are our own products." },
      { type: "process" },
      {
        type: "faq",
        items: [
          { q: "Multi-tenant or single-tenant?", a: "Multi-tenant by default because it scales and costs less to operate. We move to dedicated deployments for enterprise customers who require it." },
          { q: "Payments in Oman?", a: "Yes — we've integrated the local processors and we know the pitfalls. We'll walk you through what your customers will actually see on their statement." },
          { q: "How long to an MVP?", a: "Ten to fourteen weeks for a first customer-ready release. We architect for many from the start even when we're launching with one." },
          { q: "What does it cost to run?", a: "We model hosting cost per tenant before we build. You'll know your gross margin on a subscription before you take the first order." },
          { q: "Can we start with one customer?", a: "Yes — build for one, architect for many. The two are not the same code paths." },
          { q: "Where does the tenant data sit?", a: "We map data residency with you up-front. If your target customers ask hard questions about where their data lives, retrofit is the most expensive change there is." },
        ],
      },
    ],
    related: [
      { tag: "Services", label: "Custom software development", to: "/services/custom-software-development-company-in-oman" },
      { tag: "Services", label: "Cloud, DevOps & data residency", to: "/services/cloud-devops-oman" },
      { tag: "Solutions", label: "E-commerce marketplace", to: "/solutions/e-commerce-marketplace" },
    ],
  },

  "enterprise-software-development-company-in-oman": {
    slug: "enterprise-software-development-company-in-oman",
    path: "/services/enterprise-software-development-company-in-oman",
    navLabel: "Enterprise software",
    eyebrow: "Services",
    h1: "Enterprise Software Development Company in Oman",
    meta: {
      title: "Enterprise Software Development Company in Oman",
      description: "Enterprise portals, modules, workflow engines and legacy modernisation — joined up around the transaction, not the department.",
    },
    intro: "Large organisations do not have a software problem. They have a joining problem — twelve systems, four departments and a lot of manual re-entry between them. We build the pieces that join those systems up, without a big-bang cutover.",
    sections: [
      {
        type: "bullets", heading: "What changes at enterprise scale",
        items: [
          "More users and roles",
          "More systems that must interoperate",
          "More rules — internal policy and external regulation",
          "More scrutiny from audit, risk and the board",
          "Higher stakes when something goes wrong",
        ],
      },
      {
        type: "grid", heading: "What we build",
        items: [
          { title: "Enterprise portals", desc: "One interface across many back-end systems." },
          { title: "ERP modules", desc: "Around your existing SAP, Oracle, Odoo or Zoho — not replacing them." },
          { title: "Workflow & approval engines", desc: "With delegation, escalation and full audit." },
          { title: "Integration & middleware", desc: "REST, webhooks, scheduled sync, queues." },
          { title: "Data warehouses & reporting", desc: "One number, everywhere it appears." },
          { title: "Legacy modernisation", desc: "Wrap it, extend it, retire it — no big-bang." },
        ],
      },
      {
        type: "bullets", heading: "Non-negotiables at enterprise scale",
        items: [
          "Access control with delegation",
          "Immutable audit trails",
          "Multi-step approvals with escalation",
          "Reporting for managers, analysts and the board",
          "Integration (REST, webhooks, scheduled sync, queues)",
          "Monitoring, backups and tested DR",
          "PDPL obligations designed in, evidence exportable to your auditor",
          "SSO with your existing identity provider",
        ],
      },
      {
        type: "numbered", heading: "Our legacy modernisation approach",
        items: [
          "Audit it — code, data model, integrations, risks.",
          "Wrap it in an API so nothing outside has to know how it works.",
          "Build new modules alongside — users start moving.",
          "Migrate data and workflows in tranches.",
          "Retire the old system. No big-bang cutover.",
        ],
      },
      { type: "process" },
      {
        type: "faq",
        items: [
          { q: "Can you work with our existing ERP?", a: "Yes. We work alongside SAP, Oracle, Odoo, Zoho and custom systems. We build the module or portal that's specific to your business and integrate it with the standard package that already runs your finance and inventory." },
          { q: "Do you handle change management?", a: "Training and documentation, yes. We'll also tell you honestly where we've seen rollouts fail — the projects that die aren't the ones with bad software, they're the ones where nobody planned for the humans." },
          { q: "How do you handle security review?", a: "We expect it. We'll provide architecture documentation and a control set mapped to your framework. We work alongside whichever specialist firm you use for penetration testing." },
          { q: "Can you integrate with our on-prem systems?", a: "Yes — VPN, private link, or an on-prem middleware node depending on the shape of your network." },
          { q: "What about SSO?", a: "Azure AD, Okta, Google Workspace, Keycloak — standard. Custom SAML/OIDC providers on request." },
          { q: "How do you price enterprise work?", a: "Fixed-price sprints for well-defined phases; time-and-materials for genuinely open discovery. We tell you which is which up-front." },
        ],
      },
    ],
    related: [
      { tag: "Services", label: "ERP & HRMS development", to: "/services/erp-software-development-company-in-oman" },
      { tag: "Services", label: "Cloud, DevOps & data residency", to: "/services/cloud-devops-oman" },
      { tag: "Work", label: "Logistics ERP (Arizona, USA)", to: "/work/logistics-erp" },
    ],
  },

  "mobile-app-development-company-in-oman": {
    slug: "mobile-app-development-company-in-oman",
    path: "/services/mobile-app-development-company-in-oman",
    navLabel: "Mobile app development",
    eyebrow: "Services",
    h1: "Mobile App Development Company in Oman",
    meta: {
      title: "Mobile App Development Company in Oman",
      description: "iOS and Android apps built in Oman. Customer, driver, field-team and internal apps — bilingual, offline-tolerant, backed by our own backend.",
    },
    intro: "iOS and Android apps built in Oman. Customer apps, driver apps, field-team apps and internal tools — designed in English and Arabic, tested on the phones your users actually own, and backed by a backend we build and maintain ourselves.",
    sections: [
      {
        type: "block", heading: "Built for phones that have a hard life",
        paragraphs: [
          "Our delivery platform is built around two applications: a customer app across eleven service types with wallet, live tracking and OTP login, and a driver app with offer cascades, GPS tracking, route handling and a three-step proof of delivery — recipient name, photo and signature.",
          "They are designed for real conditions in Oman: patchy signal, heavy traffic, and cheap Android phones with poor battery life. That is a different discipline from building a portfolio piece.",
        ],
      },
      {
        type: "grid", heading: "How we build",
        items: [
          { title: "Cross-platform first", desc: "React Native / Expo, one codebase, roughly 40% less cost. Native only when the app genuinely needs it." },
          { title: "Offline-tolerant", desc: "Your driver will lose signal; the app should not lose the delivery." },
          { title: "Battery-aware", desc: "GPS tracking that does not flatten a phone by 2pm." },
          { title: "Bilingual properly", desc: "Full Arabic RTL — not a mirrored English screen." },
          { title: "Real device testing", desc: "Cheap Android phones, patchy networks, cold-start latency." },
          { title: "Store submission", desc: "We know both Apple's and Google's review pitfalls." },
        ],
      },
      {
        type: "bullets", heading: "What comes as standard",
        items: [
          "Push notifications", "OTP login", "In-app support", "Analytics & crash reporting",
          "Offline queueing", "Deep links", "Store submission", "Staged rollout",
          "Post-launch monitoring", "Battery & network profiling",
        ],
      },
      { type: "process" },
      {
        type: "faq",
        items: [
          { q: "iOS and Android from one codebase?", a: "Yes, using React Native / Expo. Native modules where a hardware feature (LiDAR, background bluetooth, specific camera modes) genuinely requires it." },
          { q: "How much cheaper is cross-platform?", a: "Typically 30–40% cheaper than two native codebases, and about half the ongoing maintenance. Every feature ships once, not twice." },
          { q: "What about Arabic?", a: "Designed as a first language, not a mirrored screen. Layout, hierarchy, iconography and line lengths change; we design both directions from the start." },
          { q: "Push notifications on iOS?", a: "APNS via our backend, with a fallback path via silent push for background sync." },
          { q: "Store approval?", a: "We handle Apple and Google submission and iterate on rejections. Every app we've submitted has shipped." },
          { q: "Ongoing support?", a: "Post-launch monitoring, crash triage and store updates are part of our Application support plans." },
        ],
      },
    ],
    related: [
      { tag: "Services", label: "Custom software development", to: "/services/custom-software-development-company-in-oman" },
      { tag: "Solutions", label: "On-demand delivery platform", to: "/solutions/on-demand-delivery-platform" },
      { tag: "Work", label: "Delivery & logistics platform", to: "/work/delivery-logistics-platform" },
    ],
  },

  "web-design-company-in-oman": {
    slug: "web-design-company-in-oman",
    path: "/services/web-design-company-in-oman",
    navLabel: "Web design & development",
    eyebrow: "Services",
    h1: "Web Design and Development Company in Oman",
    meta: {
      title: "Web Design and Development Company in Oman",
      description: "Corporate sites, web apps, e-commerce and landing pages — bilingual, fast, and built to pass Core Web Vitals.",
    },
    intro: "A website is not a brochure. It is usually the first conversation a customer has with you, and most of them have it on a phone, on mobile data, with about eight seconds of patience. We design and build the sites and web apps behind that eight seconds.",
    sections: [
      {
        type: "grid", heading: "What we build",
        items: [
          { title: "Corporate websites", desc: "Bilingual by default, editable by your team." },
          { title: "Web applications", desc: "Customer portals and internal tools." },
          { title: "E-commerce", desc: "Shopify, headless or fully custom." },
          { title: "Landing pages", desc: "For campaigns you can measure." },
          { title: "Redesigns", desc: "When the old site no longer represents the business." },
          { title: "Migrations", desc: "Off outdated platforms, without breaking SEO." },
        ],
      },
      {
        type: "bullets", heading: "What comes as standard",
        items: [
          "Core Web Vitals in the green",
          "Bilingual with proper RTL and hreflang",
          "SEO foundations and schema",
          "Accessibility",
          "A CMS you can actually use",
          "GA4 configured properly (behind consent)",
          "SSL, hardened headers, backups",
          "Uptime monitoring",
        ],
      },
      { type: "process" },
      {
        type: "faq",
        items: [
          { q: "How long?", a: "Corporate site four to six weeks. E-commerce six to ten. Complex web apps run longer and we scope them in sprints." },
          { q: "WordPress or custom?", a: "We recommend based on who will maintain it, not on what we prefer to build. Sometimes the answer is WordPress; sometimes it's a Next.js codebase in your control." },
          { q: "Do you write the content?", a: "Yes — English and Arabic. We work with you on messaging, then draft, edit and hand back." },
          { q: "Will it be fast in Oman?", a: "We measure from Oman, not from the west coast of the US. Sites are pre-rendered where possible and served from an edge close to your users." },
          { q: "Do you handle SEO?", a: "Foundations by default. Ongoing SEO is a separate engagement — see Growth & Digital Marketing." },
          { q: "Redesign or rebuild?", a: "We look at the code first. Sometimes the design needs work; sometimes the underlying site is the problem. We'll tell you which honestly." },
        ],
      },
    ],
    related: [
      { tag: "Services", label: "Growth & digital marketing", to: "/services/digital-marketing-company-in-oman" },
      { tag: "Services", label: "Design & branding", to: "/services/ui-ux-design-branding-oman" },
      { tag: "Services", label: "Application support & maintenance", to: "/services/application-support-maintenance-oman" },
    ],
  },

  "crm-software-development-company-in-oman": {
    slug: "crm-software-development-company-in-oman",
    path: "/services/crm-software-development-company-in-oman",
    navLabel: "CRM development",
    eyebrow: "Services",
    h1: "CRM Software Development Company in Oman",
    meta: {
      title: "CRM Software Development Company in Oman",
      description: "Custom CRM built around how your team actually sells. Capture, qualify, quote, chase, close, keep, report, protect.",
    },
    intro: "A CRM only works if it matches how your team actually sells. Most teams abandon theirs because it was built for someone else's process and every entry feels like paperwork. We build the CRM that gets used.",
    sections: [
      {
        type: "grid", heading: "What we offer",
        items: [
          { title: "Custom CRM development", desc: "Built to match your process." },
          { title: "CRM consulting", desc: "Sometimes the answer is configuring Zoho properly. We'll say so." },
          { title: "Mobile CRM", desc: "For teams whose phone is the main interface." },
          { title: "CRM integration", desc: "With email, WhatsApp, telephony, marketing, finance." },
          { title: "CRM migration", desc: "Off systems that no longer fit — without losing history." },
          { title: "Support & enhancement", desc: "The CRM you already have, actually working." },
        ],
      },
      {
        type: "grid", heading: "Types of CRM we build", columns: 4,
        items: [
          { title: "Sales-led", desc: "The pipeline is the whole point." },
          { title: "Service-led", desc: "Jobs, tickets and repeat visits." },
          { title: "Field-led", desc: "The phone is the main interface, the laptop is the exception." },
          { title: "Account-led", desc: "A handful of large B2B relationships where the money is in renewal." },
        ],
      },
      {
        type: "stack", heading: "Industry CRMs we've built",
        items: ["Real estate", "Healthcare", "Automotive", "Logistics", "Education", "Retail", "Construction", "Hospitality"],
      },
      {
        type: "block", heading: "The features, organised by what a salesperson is actually doing",
        subheading: "Not by feature category. This is the difference between a CRM that gets used and one that gets ignored.",
        items: [
          { title: "Capture", desc: "Enquiries from your website, WhatsApp, phone and the showroom door all land in one queue, tagged by source. After three months you will finally know which marketing spend works." },
          { title: "Qualify", desc: "Scoring rules you define, so your best closer is not spending Tuesday morning on someone who was never going to buy." },
          { title: "Quote", desc: "Build from your price list, send it, see when it was opened, turn it into an invoice without retyping a line." },
          { title: "Chase", desc: "Follow-up sequences that fire on their own over WhatsApp and email, and stop the second the customer replies. This is where most sales are lost and the cheapest thing to fix." },
          { title: "Close", desc: "Stages that match your real sales cycle, with a forecast your manager can take to a board meeting." },
          { title: "Keep", desc: "Renewal dates, service reminders and repeat-purchase prompts. The second sale costs a fraction of the first and almost nobody chases it." },
          { title: "Report", desc: "By salesperson, product, region and source, exported to Excel the moment your accountant asks." },
          { title: "Protect", desc: "Encryption, role-based access, and an audit trail of every change to every record." },
        ],
        columns: 2,
      },
      { type: "callout", text: "Arabic throughout — a real bilingual interface, so nobody is working in their second language all day." },
      { type: "process" },
      {
        type: "faq",
        items: [
          { q: "Custom or configured?", a: "We honestly recommend based on your process. If a well-configured Zoho or HubSpot fits, we say so — and we'll configure it for you if you want." },
          { q: "Do you migrate our existing data?", a: "Yes — including notes, attachments and full history. Nothing gets left behind." },
          { q: "WhatsApp Business integration?", a: "Yes, using WhatsApp Business API. Chats, templates and delivery receipts flow into the record." },
          { q: "Mobile?", a: "Every CRM we build ships with a mobile app on day one. It's often the more-used interface." },
          { q: "How long?", a: "A focused CRM in six to eight weeks. Multi-industry, multi-team CRMs run three to five months." },
        ],
      },
    ],
    related: [
      { tag: "Services", label: "ERP & HRMS development", to: "/services/erp-software-development-company-in-oman" },
      { tag: "Services", label: "AI Lab", to: "/ai-lab" },
      { tag: "Solutions", label: "Showroom management", to: "/solutions/showroom-management" },
    ],
  },
};
